/**
 * CMS SDK - MediaLibrary 组件
 * 媒体库管理
 */

import React, { useState, useCallback, useRef } from 'react';
import { t } from '../i18n';
import { MediaType } from '../types';

/**
 * 媒体库组件
 * @param {Object} props - 组件属性
 */
export function MediaLibrary({
  mediaList = [],
  loading = false,
  uploading = false,
  uploadProgress = 0,
  onUpload,
  onDelete,
  onSelect,
  multiple = false,
  selectedIds = [],
  accept = 'image/*,video/*,audio/*',
  className = '',
}) {
  const [viewMode, setViewMode] = useState('grid'); // grid | list
  const [filterType, setFilterType] = useState('all');
  const fileInputRef = useRef(null);

  // 过滤后的媒体列表
  const filteredMedia = filterType === 'all' 
    ? mediaList 
    : mediaList.filter(m => m.type === filterType);

  // 点击上传
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // 文件选择
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0 && onUpload) {
      files.forEach(file => onUpload(file));
    }
    e.target.value = '';
  }, [onUpload]);

  // 拖拽处理
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && onUpload) {
      files.forEach(file => onUpload(file));
    }
  }, [onUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  // 选择媒体
  const handleSelect = useCallback((media) => {
    if (!onSelect) return;
    if (multiple) {
      if (selectedIds.includes(media.id)) {
        onSelect(selectedIds.filter(id => id !== media.id));
      } else {
        onSelect([...selectedIds, media.id]);
      }
    } else {
      onSelect([media.id]);
    }
  }, [onSelect, multiple, selectedIds]);

  // 格式化文件大小
  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // 获取媒体图标
  const getMediaIcon = (type) => {
    switch (type) {
      case MediaType.IMAGE: return '🖼️';
      case MediaType.VIDEO: return '🎬';
      case MediaType.AUDIO: return '🎵';
      case MediaType.DOCUMENT: return '📄';
      default: return '📁';
    }
  };

  return (
    <div className={`cms-media-library ${className}`}>
      {/* 工具栏 */}
      <div className="cms-media-toolbar">
        <div className="cms-media-filters">
          <select 
            className="cms-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">全部</option>
            <option value={MediaType.IMAGE}>{t('image')}</option>
            <option value={MediaType.VIDEO}>{t('video')}</option>
            <option value={MediaType.AUDIO}>{t('audio')}</option>
            <option value={MediaType.DOCUMENT}>{t('document')}</option>
          </select>
        </div>
        
        <div className="cms-media-actions">
          <div className="cms-view-toggle">
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              ⊞
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              ☰
            </button>
          </div>
          <button 
            className="cms-btn cms-btn-primary"
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {t('uploadMedia')}
          </button>
        </div>
      </div>

      {/* 上传区域 */}
      <div 
        className="cms-media-dropzone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        
        {uploading ? (
          <div className="cms-upload-progress">
            <div 
              className="cms-progress-bar"
              style={{ width: `${uploadProgress}%` }}
            />
            <span>{t('uploadProgress')}: {uploadProgress}%</span>
          </div>
        ) : (
          <div className="cms-dropzone-hint">
            <span className="cms-dropzone-icon">📁</span>
            <span>{t('dragDropHint')}</span>
          </div>
        )}
      </div>

      {/* 媒体列表 */}
      {loading ? (
        <div className="cms-media-loading">
          <div className="cms-loading-spinner" />
          <span>{t('loading')}</span>
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="cms-media-empty">
          <span className="cms-empty-icon">📂</span>
          <span>{t('noMedia')}</span>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="cms-media-grid">
          {filteredMedia.map(media => (
            <div 
              key={media.id}
              className={`cms-media-item ${selectedIds.includes(media.id) ? 'selected' : ''}`}
              onClick={() => handleSelect(media)}
            >
              <div className="cms-media-preview">
                {media.type === MediaType.IMAGE ? (
                  <img src={media.thumbnail_url || media.url} alt={media.name} />
                ) : (
                  <span className="cms-media-icon">{getMediaIcon(media.type)}</span>
                )}
                {selectedIds.includes(media.id) && (
                  <span className="cms-media-check">✓</span>
                )}
              </div>
              <div className="cms-media-info">
                <span className="cms-media-name">{media.name}</span>
                <span className="cms-media-size">{formatSize(media.size)}</span>
              </div>
              {onDelete && (
                <button 
                  className="cms-media-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(media.id);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="cms-media-list">
          {filteredMedia.map(media => (
            <div 
              key={media.id}
              className={`cms-media-row ${selectedIds.includes(media.id) ? 'selected' : ''}`}
              onClick={() => handleSelect(media)}
            >
              <div className="cms-media-row-preview">
                {media.type === MediaType.IMAGE ? (
                  <img src={media.thumbnail_url || media.url} alt={media.name} />
                ) : (
                  <span className="cms-media-icon">{getMediaIcon(media.type)}</span>
                )}
              </div>
              <div className="cms-media-row-info">
                <span className="cms-media-name">{media.name}</span>
                <span className="cms-media-meta">
                  {formatSize(media.size)} · {media.mime_type}
                </span>
              </div>
              {onDelete && (
                <button 
                  className="cms-media-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(media.id);
                  }}
                >
                  {t('delete')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MediaLibrary;
